import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Link from '@jetbrains/ring-ui/components/link/link'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import * as React from 'react'

import {useIsAutodetectTimeZone} from '../../../hooks/useIsAutodetectTimeZone'
import {useDistanceToNow} from '../../../hooks/useTimeSelector'
import IconButton from '../../../library/components/IconButton/IconButton'
import SvgIcon from '../../../library/components/SvgIcon/SvgIcon'
import {parseDateToObj} from '../../../utils/dateTimeUtils'
import Popup from '../Popup/Popup.lazy'

import styles from './BuildWarning.css'

const DEFAULT_POPUP_DIRECTIONS = [Directions.RIGHT_BOTTOM]
type Props = {
  running?: boolean | null | undefined
  composite?: boolean | null | undefined
  hasExecutor?: boolean | null | undefined
  detachedFromAgent?: boolean | null | undefined
  agentConnected?: boolean | null | undefined
  agentName?: string | null | undefined
  lastActivityTime?: string | null | undefined
  probablyHanging?: boolean | null | undefined
  outdated?: boolean | null | undefined
  outdatedReasonBuildNumber?: string | null | undefined
  outdatedReasonBuildUrl?: string | null | undefined
  className?: string
  inline?: boolean | null | undefined
}

type PropsContainer = {
  isAutodetectTimeZone: boolean
}

function BuildWarningComponent(props: Props & PropsContainer) {
  const {
    className,
    running,
    composite,
    hasExecutor,
    detachedFromAgent,
    agentConnected,
    probablyHanging,
    outdated,
    inline,
    lastActivityTime,
    isAutodetectTimeZone,
  } = props
  const lastActivityDateObj =
    lastActivityTime != null
      ? parseDateToObj(lastActivityTime, isAutodetectTimeZone)
      : lastActivityTime
  const lastActivityFromNow = useDistanceToNow(lastActivityTime)
  const formattedLastActivityDate =
    lastActivityDateObj != null
      ? `${lastActivityDateObj.format('D MMM YY LT')} (${lastActivityFromNow})`
      : null

  function renderNotConnectedAgentMessage(): React.ReactNode {
    const {agentName} = props
    return (
      <>
        <span className={styles.warning}>{`Build agent ${
          agentName || ''
        } was disconnected while running a build`}</span>
        {
          'Please check the agent and network connectivity. Either ensure the agent is connected or stop the build'
        }
        {formattedLastActivityDate != null &&
          `. Last message was received on: ${formattedLastActivityDate}`}
      </>
    )
  }

  function renderHangingMessage(): React.ReactNode {
    if (composite === true) {
      return <span className={styles.warning}>{'Some dependencies of this build are hanging'}</span>
    } else {
      const agentMention = detachedFromAgent !== true ? ' from the agent' : ''
      return (
        <>
          <span className={styles.warning}>{'This build is hanging'}</span>
          {formattedLastActivityDate != null &&
            `Last message${agentMention} was received on: ${formattedLastActivityDate}`}
        </>
      )
    }
  }

  function renderOutdatedMessage(): React.ReactNode {
    const {outdatedReasonBuildNumber, outdatedReasonBuildUrl} = props
    const buildLabel = outdatedReasonBuildNumber != null ? `#${outdatedReasonBuildNumber}` : null
    return (
      <>
        <span className={styles.warning}>{'This build is outdated'}</span>
        {'More recent build '}
        {buildLabel != null &&
          (outdatedReasonBuildUrl != null ? (
            <Link href={outdatedReasonBuildUrl}>{buildLabel}</Link>
          ) : (
            buildLabel
          ))}
        {' is already finished'}
      </>
    )
  }

  if (running !== true) {
    return null
  }

  let message

  if (
    composite !== true &&
    detachedFromAgent !== true &&
    agentConnected !== true &&
    hasExecutor !== true
  ) {
    message = renderNotConnectedAgentMessage()
  } else if (probablyHanging === true) {
    message = renderHangingMessage()
  } else if (outdated === true) {
    message = renderOutdatedMessage()
  }

  if (message != null) {
    if (inline) {
      return (
        <div className={className}>
          <SvgIcon icon="warning" className={styles.inlineIcon} />
          {message}
        </div>
      )
    }

    const anchor = (
      <IconButton
        icon="warning"
        title=""
        aria-label="Warning"
        className={classNames(styles.button, className)}
        iconClassName={styles.icon}
      />
    )
    return (
      <Dropdown
        anchor={anchor}
        hoverShowTimeOut={300}
        hoverHideTimeOut={300}
        clickMode={false}
        hoverMode
      >
        <Popup directions={DEFAULT_POPUP_DIRECTIONS} left={5}>
          <div className={styles.popup}>{message}</div>
        </Popup>
      </Dropdown>
    )
  }

  return null
}

const BuildWarning = React.memo(BuildWarningComponent)

function BuildWarningContainer(props: Props) {
  const isAutodetectTimeZone = useIsAutodetectTimeZone()

  return <BuildWarning {...props} isAutodetectTimeZone={isAutodetectTimeZone} />
}

export default React.memo(BuildWarningContainer)
